<template>
  <div class="w-full flex flex-col px-1 pt-1 pb-3 border border-border-normal" style="border-radius: 20px 20px 8px 8px;">
    <!-- Header (Ranked Ads Category Selector) -->
    <div class="w-full flex items-center p-2 justify-between flex-wrap gap-y-2">
      <div class="flex items-center gap-2 flex-shrink-0">
        <button v-for="option in rankedAdCategoryOptions" :key="`ranked-category-btn-${option.key}`"
        class="group flex items-center gap-1 p-1 rounded-md transition-colors duration-100"
        :class="option.isActive ? 'text-text-muted' : 'text-text-subdued hover:bg-neutral-25 hover:text-text-muted'"
        @click="toggleRankedAdsCategory(option.key)">
          <component :is="option.icon" class="transition-colors duration-100"
          :class="option.isActive ? 'text-icon-normal' : 'text-text-subdued group-hover:text-icon-normal'" />
          <BaseText type="label" size="sm">
            {{ option.label }}
          </BaseText>
          <BaseText v-if="rankedAdCounts.all > 0" type="label" size="xs" class="p-0.5 transition-colors duration-100 fade-in" style="border-radius: 20px; min-width: 20px;"
          :class="option.isActive ? 'text-text-normal bg-neutral-25' : 'text-text-subdued bg-neutral-25 group-hover:text-text-normal group-hover:bg-neutral-50'">
            {{ option.count }}
          </BaseText>
        </button>
      </div>
      <div class="flex items-center gap-2">
        <button class="px-2 py-1 rounded-md transition-colors duration-100 hover:bg-neutral-25"
        @click="$emit('analyzeAdRanks')">
          <BaseText type="label" size="sm" class="text-text-muted">
            View All Rankings
          </BaseText>
        </button>
        <!-- Scroll Buttons -->
        <div class="flex items-center flex-shrink-0">
          <button class="left-scroll p-1 rounded-l-md border border-border-normal transition-colors duration-100" :disabled="!leftScrollBtnEnabled"
          :class="leftScrollBtnEnabled ? 'text-text-muted hover:bg-neutral-50' : 'text-text-disabled cursor-default'"
          @click="handlePreviewScroll('left')">
            <svg ref="leftChevron" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M11.667 13.3337L8.92291 10.5896C8.59748 10.2641 8.59748 9.73651 8.92291 9.41107L11.667 6.66699" 
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="right-scroll p-1 rounded-r-md border-r border-t border-b border-border-normal transition-colors duration-100" 
          :disabled="!rightScrollBtnEnabled" :class="rightScrollBtnEnabled ? 'text-text-muted hover:bg-neutral-50' : 'text-text-disabled cursor-default'"
          @click="handlePreviewScroll('right')">
            <svg ref="rightChevron" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M8.33301 13.3337L11.0771 10.5896C11.4025 10.2641 11.4025 9.73651 11.0771 9.41107L8.33301 6.66699" 
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <!-- Ads Display -->
    <div class="relative w-full flex gap-x-1 p-1.5 rounded-2xl bg-neutral-25" style="height: 368px;">
      <div class="ranked-ads-scroll-container absolute top-1.5 -left-1 -right-1 -bottom-3 flex gap-x-1 min-w-0 px-2.5"
      ref="rankedAdsScrollContainer" :class="{ 'loading': loadingRankedAds }">
        <!-- Skeletons -->
        <template v-if="loadingRankedAds">
          <div v-for="i in 10" :key="`top-ranked-ad-skeleton-${i}`" style="width: 260px; height: 358px;"
          class="flex flex-col p-1 bg-white rounded-xl card-shadow flex-shrink-0">
            <div class="flex items-center gap-2 px-2 py-2.5">
              <div class="skeleton w-7 h-7 rounded-md" />
              <div class="skeleton w-32 h-5 rounded-md" />
            </div>
            <div class="w-full px-1"><div class="skeleton w-full rounded-md" style="height: 30px" /></div>
            <div class="skeleton w-full rounded-md mt-1.5" style="height: 260px;" />
          </div>
        </template>
        <div v-else-if="!rankedAdsList.length" class="w-full flex flex-col items-center justify-center">
          <TrophyIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-normal mt-3">
            No Rank Data Available
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal mt-1.5 text-center" style="max-width: 200px;">
            This brand has no active ads that are currently ranked.
          </BaseText>
        </div>
        <!-- Ad Cards -->
        <template v-else>
          <LibraryCard 
            v-for="ad in rankedAdsList" :key="`top-ranked-ad-${ad.id}`"
            class="flex-shrink-0 fade-in"
            style="width: 260px; height: 358px;"
            :advertisement="ad"
            discovery
            hide-creative-count
            show-spyder-ad-rank
            force-square-ratio
            force-disable-ad-copy
            forceDisableHeadlineCTA
            hide-save-button
            border-radius="12px"
            @openDetails="$emit('openAdDetails', ad)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import LibraryCard from '../../library/LibraryCard.vue'

// Icons
import AdRankIcon from '../../globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import EvergreenIcon from '../../globals/Icons/SpyderV2Icons/EvergreenIcon.vue'
import BreakoutIcon from '../../globals/Icons/SpyderV2Icons/BreakoutIcon.vue'
import TrophyIcon from '../../globals/Icons/TrophyIcon.vue'

export default {
  name: 'AdRankPreview',
  components: {
    LibraryCard,
    TrophyIcon
  },
  props: {
    pageId: {
      required: true
    }
  },
  data () {
    return {
      rankedAdsCategory: 'all',
      rankedAds: { all: [], evergreen: [], breakout: [] },
      rankedAdCounts: { all: 0, evergreen: 0, breakout: 0 },
      loadingRankedAds: true,
      leftScrollBtnEnabled: false,
      rightScrollBtnEnabled: true,
    }
  },
  mounted () {
    const container = this.$refs.rankedAdsScrollContainer
    if (container) container.addEventListener('scroll', this.updateScrollButtons, { passive: true })
    window.addEventListener('resize', this.updateScrollButtons)
  },
  beforeDestroy () {
    const container = this.$refs.rankedAdsScrollContainer
    if (container) container.removeEventListener('scroll', this.updateScrollButtons)
    window.removeEventListener('resize', this.updateScrollButtons)
  },
  watch: {
    pageId: {
      handler (newPageId) {
        if (!newPageId) return
        this.rankedAdsCategory = 'all'
        this.rankedAds = { all: [], evergreen: [], breakout: [] }
        this.rankedAdCounts = { all: 0, evergreen: 0, breakout: 0 }
        this.fetchTopRankedAds('all', 10)
      },
      immediate: true
    }
  },
  computed: {
    rankedAdCategoryOptions () {
      return [
        {
          key: 'all',
          label: 'Top Ranked Ads',
          icon: AdRankIcon,
          isActive: this.rankedAdsCategory === 'all',
          count: this.rankedAds.all.length,
        },
        ...(this.rankedAdCounts?.evergreen > 0 ? [{
          key: 'evergreen',
          label: 'Evergreen',
          icon: EvergreenIcon,
          isActive: this.rankedAdsCategory === 'evergreen',
          count: this.rankedAdCounts.evergreen,
        }] : []),
        ...(this.rankedAdCounts?.breakout > 0 ? [{
          key: 'breakout',
          label: 'Breakout',
          icon: BreakoutIcon,
          isActive: this.rankedAdsCategory === 'breakout',
          count: this.rankedAdCounts.breakout,
        }] : []),
      ]
    },
    rankedAdsList () {
      return this.rankedAds[this.rankedAdsCategory] || []
    }
  },
  methods: {
    toggleRankedAdsCategory (category) {
      if (this.loadingRankedAds) return
      this.rankedAdsCategory = category
      this.$nextTick(this.updateScrollButtons)
      if (this.rankedAds[category].length) return
      const limit = category === 'all' ? 10 : this.rankedAdCounts[category] || 10
      this.fetchTopRankedAds(category, limit)
    },
    async fetchTopRankedAds (category, limit) {
      // Get the top 3 ranked ads for the brand
      if (!this.pageId) return
      this.loadingRankedAds = true
      try {
        const { data } = await ForeplayAPI.Brands.getBrandRankedAds(
          this.pageId,
          category,
          { limit, page: 0 }
        )
        this.rankedAds[category] = data.ads
        this.rankedAdCounts = data.counts
      } catch (error) {
        console.error('Error fetching top ranked ads:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch top ranked ads. See console for details.'
        })
      } finally {
        this.loadingRankedAds = false
        this.$nextTick(this.updateScrollButtons)
      }
    },
    updateScrollButtons () {
      const container = this.$refs.rankedAdsScrollContainer
      if (!container) return
      const { scrollLeft, clientWidth, scrollWidth } = container
      this.leftScrollBtnEnabled = scrollLeft > 1
      this.rightScrollBtnEnabled = scrollLeft < scrollWidth - clientWidth - 1
    },
    handlePreviewScroll (direction) {
      // Animate the chevron icon bounce
      const chevronIcon = direction === 'left' ? this.$refs.leftChevron : this.$refs.rightChevron
      const bounceAnimClass = direction === 'left' ? 'bounce-left-anim' : 'bounce-right-anim'
      chevronIcon.classList.add(bounceAnimClass)
      setTimeout(() => { chevronIcon.classList.remove(bounceAnimClass) }, 200)

      // Scroll the container
      const container = this.$refs.rankedAdsScrollContainer
      if (!container || this.loadingRankedAds) return

      const EDGE_GAP = 4
      const cards = Array.from(container.children)
      const visibleLeft = container.scrollLeft
      const visibleRight = visibleLeft + container.clientWidth
      let target

      if (direction === 'right') {
        const candidates = cards.filter((card) => card.offsetLeft >= visibleRight)
        const secondCard = candidates[1]
        if (secondCard && secondCard !== cards[cards.length - 1]) {
          target = secondCard.offsetLeft + secondCard.offsetWidth - container.clientWidth + EDGE_GAP
        } else {
          target = container.scrollWidth - container.clientWidth
        }
      } else {
        const candidates = cards.filter((card) => card.offsetLeft + card.offsetWidth <= visibleLeft).reverse()
        const secondCard = candidates[1]
        if (secondCard && secondCard !== cards[0]) {
          target = secondCard.offsetLeft - EDGE_GAP
        } else {
          target = 0
        }
      }

      container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.ranked-ads-scroll-container {
  overflow-x: scroll;
}
.ranked-ads-scroll-container::-webkit-scrollbar {
  height: 12px;
  width: 12px;
}
.ranked-ads-scroll-container::-webkit-scrollbar-thumb {
  background-color: #DFE1E6;
  border-radius: 10px;
  border: 3px solid #ffffff;
  cursor: pointer;
}
.ranked-ads-scroll-container.loading::-webkit-scrollbar-thumb {
  background-color: #ffffff;
}
.ranked-ads-scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: #C1C7D0;
}
.card-shadow {
  box-shadow: 0px 0px 0px 1px rgba(24, 48, 123, 0.04), 0px 1px 1px -0.5px rgba(26, 48, 84, 0.04), 0px 2px 2px -1px rgba(26, 48, 84, 0.03), 0px 3px 3px -1.5px rgba(26, 48, 84, 0.03), 0px 5px 5px -2.5px rgba(26, 48, 84, 0.02), 0px 8px 8px -4px rgba(26, 48, 84, 0.02);
}

.bounce-left-anim {
  animation: bounceLeft 200ms ease-in-out;
}
.bounce-right-anim {
  animation: bounceRight 200ms ease-in-out;
}
@keyframes bounceRight {
  0% { transform: translateX(0); }
  50% { transform: translateX(2px); }
  100% { transform: translateX(0); }
}
@keyframes bounceLeft {
  0% { transform: translateX(0); }
  50% { transform: translateX(-2px); }
  100% { transform: translateX(0); }
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>